import { Step, Workspace, WorkspaceSetType } from '../shared/types/riskWorkspace.types'

export const CREATE_EDIT_WORKSPACE_STEPS: Record<Step, string> = {
  overview: 'Overview',
  hazards_and_scenarios: 'Hazards and scenarios',
  capitals: 'Capitals',
  contextual_layers: 'Contextual layers',
  live_layers: 'Live layers',
  areas: 'Areas',
  review_and_save: 'Review and save',
}

// Ordered step keys for the create/edit workspace flow. Pass false for a step to exclude it
// (e.g. live_layers when the REX_LIVE flag is off, contextual_layers when there are none to pick from)
export const getWorkspaceStepKeys = (stepVisibility: Partial<Record<Step, boolean>> = {}): Step[] =>
  (Object.keys(CREATE_EDIT_WORKSPACE_STEPS) as Step[]).filter(
    (step) => stepVisibility[step] ?? true,
  )

// Excludes the Social/Economic presets, which can always be added on top
export const MAX_CUSTOM_CAPITAL_SETS = 5

export const MAX_SET_OR_GROUP_NAME_LENGTH = 40

export const EMPTY_SET = {
  name: '',
  groups: [],
}

export const EMPTY_GROUP = {
  name: '',
  layers: [],
}

export const CAPITAL_SET_PRESETS = {
  social: 'Social',
  economic: 'Economic',
} as const

export type CapitalSetPresetName = (typeof CAPITAL_SET_PRESETS)[keyof typeof CAPITAL_SET_PRESETS]

export const isCapitalSetPreset = (name: string): name is CapitalSetPresetName =>
  name === CAPITAL_SET_PRESETS.social || name === CAPITAL_SET_PRESETS.economic

export const countCustomCapitalSets = (sets: { name: string }[]): number =>
  sets.filter(({ name }) => !isCapitalSetPreset(name)).length

// Case-insensitive check used to stop a custom capital set from being named after a preset,
// regardless of casing (e.g. "social capital"), even though presets themselves are matched exactly.
export const isReservedCapitalSetName = (name: string): boolean => {
  const normalized = name.trim().toLowerCase()
  return (
    normalized === CAPITAL_SET_PRESETS.social.toLowerCase() ||
    normalized === CAPITAL_SET_PRESETS.economic.toLowerCase()
  )
}

export const EMPTY_WORKSPACE: Workspace = {
  name: '',
  description: '',
  tags: [],
  visibility: 'private',
  scenario_sets: [],
  capital_sets: [],
  contextual_layers: [],
  live_groups: [],
  areas: {
    area_type: 'Whole region',
    areas: [],
  },
  layers: {},
}

export const EMPTY_WORKSPACE_STEPS: Record<string, boolean> = {
  overview: false,
  hazards_and_scenarios: false,
  capitals: false,
  contextual_layers: false,
  live_layers: false,
  areas: false,
  review_and_save: false,
}

export const MAP_SET_OR_GROUP_NAME: Record<
  Step | 'hazard_sets' | WorkspaceSetType,
  WorkspaceSetType | undefined
> = {
  // page steps (Step)
  overview: undefined,
  hazards_and_scenarios: 'scenario_sets',
  capitals: 'capital_sets',
  contextual_layers: 'contextual_layers',
  live_layers: 'live_groups',
  areas: undefined,
  review_and_save: undefined,
  // component step_type/set_type spellings
  hazard_sets: 'scenario_sets',
  scenario_sets: 'scenario_sets',
  capital_sets: 'capital_sets',
  live_groups: 'live_groups',
}
